/* eslint-disable react/prop-types */
import { AlertTriangle, RotateCcw, Utensils, User, Calendar, CreditCard } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

/**
 * Modern Cancel/Confirmation dialog with rich reservation context.
 *
 * @param {object} props
 * @param {boolean} props.isOpen
 * @param {() => void} props.onClose
 * @param {() => void} props.onConfirm
 * @param {string} props.title
 * @param {string} props.description
 * @param {React.ReactNode} props.icon
 * @param {boolean} props.isLoading
 * @param {object} props.reservation - Optional reservation object to show summary info
 * @param {string} props.confirmText - Label for confirmation button (default: "Yes, Cancel")
 * @param {string} props.cancelText - Label for dismiss button (default: "Keep Reservation")
 */
export default function CancelDialog({
  isOpen,
  onClose,
  onConfirm,
  title = "Cancel Reservation?",
  description = "",
  icon = <AlertTriangle className="h-6 w-6" />,
  isLoading = false,
  reservation = null,
  confirmText = "Yes, Cancel",
  cancelText = "Keep Reservation",
}) {
  const isDepositPaid = reservation?.is_deposit_paid == true || reservation?.is_deposit_paid === 1;
  const depositStatus = reservation?.deposit_payment_info?.status?.toLowerCase();
  const isRefunded = depositStatus === "refunded";
  const hasPaidDeposit = isDepositPaid && !isRefunded && Number(reservation?.advance) > 0;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[440px] bg-cblack4 border-gray-800 text-white p-6 shadow-2xl animate-fadeIn">
        <DialogHeader className="flex flex-col justify-center items-center gap-3 text-center">
          {/* Animated/Glowing danger icon badge */}
          <div className="relative flex justify-center items-center h-12 w-12 rounded-full bg-cred/15 text-cred border border-cred/30 shadow-inner">
            <span className="[&>svg]:h-6 [&>svg]:w-6">{icon}</span>
          </div>

          <DialogTitle className="text-white text-lg font-bold tracking-wide">
            {title}
          </DialogTitle>

          {description ? (
            <DialogDescription className="text-cwhite3 text-xs leading-relaxed max-w-[340px]">
              {description}
            </DialogDescription>
          ) : (
            <DialogDescription className="text-cwhite3 text-xs leading-relaxed max-w-[340px]">
              Are you sure you want to cancel this reservation? This action will update the booking status.
            </DialogDescription>
          )}
        </DialogHeader>

        {/* Reservation Details Box if reservation prop is passed */}
        {reservation && (
          <div className="mt-2 space-y-3">
            {/* Quick Metadata summary */}
            <div className="bg-cblack3 p-3.5 rounded-xl border border-gray-800 space-y-2 text-xs">
              <div className="flex justify-between items-center pb-2 border-b border-gray-800/60">
                <span className="text-cwhite3">Reference</span>
                <span className="font-semibold text-white font-mono">
                  {reservation.unique_id || `#${reservation.id}`}
                </span>
              </div>

              {reservation.customer?.full_name && (
                <div className="flex justify-between items-center">
                  <span className="text-cwhite3 flex items-center gap-1.5">
                    <User className="h-3.5 w-3.5 text-cyellow" />
                    Customer
                  </span>
                  <span className="font-medium text-white">
                    {reservation.customer.full_name}
                  </span>
                </div>
              )}

              {reservation.tables && reservation.tables.length > 0 && (
                <div className="flex justify-between items-center">
                  <span className="text-cwhite3 flex items-center gap-1.5">
                    <Utensils className="h-3.5 w-3.5 text-cblue" />
                    Allocated Table(s)
                  </span>
                  <span className="font-medium text-cyellow">
                    {reservation.tables.map((t) => t.name).join(", ")}
                  </span>
                </div>
              )}
            </div>

            {/* Refund Alert Banner if deposit was paid */}
            {hasPaidDeposit && (
              <div className="flex items-start gap-2.5 p-3 rounded-lg border border-cyellow/30 bg-cyellow/10 text-xs text-cwhite2">
                <RotateCcw className="h-4 w-4 text-cyellow shrink-0 mt-0.5" />
                <div className="space-y-0.5">
                  <p className="font-semibold text-cyellow">
                    Advance Deposit Refund (£{reservation.advance})
                  </p>
                  <p className="text-cwhite3 text-[11px] leading-tight">
                    Cancelling will automatically trigger a refund of £{reservation.advance} to the customer via {reservation?.deposit_payment_info?.method === "terminal" ? "Card Terminal" : "original payment method"}.
                  </p>
                </div>
              </div>
            )}
          </div>
        )}

        <DialogFooter className="mt-4">
          <div className="grid grid-cols-2 gap-3 w-full pt-1">
            <Button
              type="button"
              variant="outline"
              className="bg-cblack3 hover:bg-cblack7 border-gray-800 text-cwhite3 hover:text-white font-medium py-2 rounded-lg transition"
              onClick={onClose}
              disabled={isLoading}
            >
              {cancelText}
            </Button>
            <Button
              type="button"
              isLoading={isLoading}
              className="bg-cred hover:bg-cred/90 text-white font-bold py-2 rounded-lg shadow-md transition"
              onClick={() => {
                onConfirm?.();
              }}
            >
              {confirmText}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
